import { ChangeDetectorRef, EventEmitter, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { Input, OnDestroy } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MobilePhoneFormControls } from '@app/sales/modules/mobilephone/components/mobilephone-search/mobilephone-details/mobilephone-details.component';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { Subscription } from 'rxjs';
import { ExistingOfferingUpgradeComponent } from './existing-offering-upgrade/existing-offering-upgrade.component';
import { Product } from '@app/shared/models/product.model';
import { EquipmentPaymentType } from '@app/sales/enums/equipment-payment-type';
import { MobilePaymentType } from '@app/sales/enums/mobile-payment-type';
import { MatTooltip } from '@angular/material/tooltip';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { MatRadioGroup } from '@angular/material/radio';
import { select } from '@ngxs/store';
import { OfferingSelectors } from '@app/sales/states/offering/offering.selectors';

@Component({
  selector: 'app-equipment-voice-number-type',
  imports: [IceSelectBoxComponent, MatTooltip, MatRadioGroup, ExistingOfferingUpgradeComponent, ReactiveFormsModule],
  templateUrl: './equipment-voice-number-type.component.html',
  styleUrls: ['./equipment-voice-number-type.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class EquipmentVoiceNumberTypeComponent implements OnInit, OnDestroy, OnChanges {
  formGroupDirective = inject(FormGroupDirective);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dealerOfferings = select(OfferingSelectors.getAllOfferings);

  EquipmentFormControls = MobilePhoneFormControls;

  @Input() existingVoiceProducts: VoiceProduct[];
  @Input() voiceProductsAvailableForUpgrade: VoiceProduct[];
  @Input() productInCart: Product;
  @Input() equipmentPaymentType: EquipmentPaymentType | MobilePaymentType;
  @Input() existingProductsAvailableForEquipmentUpgrade: VoiceProduct[];
  @Input() isSubscriptionLimitReached: boolean;
  @Output() getExistingVoiceProductsDetails = new EventEmitter();

  subscriptions$: Subscription[] = [];
  form: FormGroup;
  newOfferingId: string;
  subscriptionToUpgrade: string;

  get isBelowTheLineOffering(): boolean {
    const dealerOfferings = this.dealerOfferings();
    return !!this.newOfferingId && !!dealerOfferings?.length && !dealerOfferings.some(o => o.id === this.newOfferingId);
  }

  ngOnInit() {
    this.form = this.formGroupDirective.form;
    this.voiceProductsAvailableForUpgrade = (
      !!this.voiceProductsAvailableForUpgrade?.length
        ? this.voiceProductsAvailableForUpgrade
        : this.existingVoiceProducts?.filter(
            p => !p.mobileProductId || (this.productInCart && p.mobileProductId === this.productInCart.id)
          )
    )?.filter(voiceProduct => !VoiceProduct.isActivationInProgress(voiceProduct));
    this.subscriptions$.push(
      this.form.get(this.EquipmentFormControls.IsSubscriptionUpgrade).valueChanges.subscribe(isSubscriptionUpgrade => {
        if (this.form.value[this.EquipmentFormControls.IsSubscriptionUpgrade] !== isSubscriptionUpgrade) {
          this.initialize(isSubscriptionUpgrade);
        }
      })
    );

    this.newOfferingId = this.form.get(this.EquipmentFormControls.NewOfferingId).value;
    this.clearNewNumberIfBelowTheLine();
    this.subscriptions$.push(
      this.form.get(this.EquipmentFormControls.NewOfferingId).valueChanges.subscribe(newOfferingId => {
        this.newOfferingId = newOfferingId;
        this.clearNewNumberIfBelowTheLine();
        this.changeDetectorRef.detectChanges();
      })
    );

    this.subscriptionToUpgrade = this.form.get(this.EquipmentFormControls.SubscriptionIdToUpgrade).value;
    this.subscriptions$.push(
      this.form.get(this.EquipmentFormControls.SubscriptionIdToUpgrade).valueChanges.subscribe(newSubscriptionToUpgrade => {
        this.subscriptionToUpgrade = newSubscriptionToUpgrade;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.initialize(this.form.get(this.EquipmentFormControls.IsSubscriptionUpgrade).value);
  }

  onResetSubscriptionIdToUpgrade() {
    this.form.get(this.EquipmentFormControls.SubscriptionIdToUpgrade).setValue(null);
    this.changeDetectorRef.detectChanges();
  }

  private clearNewNumberIfBelowTheLine() {
    const isSubscriptionUpgradeControl = this.form.get(this.EquipmentFormControls.IsSubscriptionUpgrade);
    if (this.isBelowTheLineOffering && isSubscriptionUpgradeControl.value === false) {
      isSubscriptionUpgradeControl.setValue(null);
    }
  }

  private initialize(isSubscriptionUpgrade: boolean) {
    if (isSubscriptionUpgrade) {
      this.form.get(this.EquipmentFormControls.SubscriptionIdToUpgrade).enable();
      this.getExistingVoiceProductsDetails.emit();
    } else {
      this.form.get(this.EquipmentFormControls.SubscriptionIdToUpgrade).setValue(null);
      this.form.get(this.EquipmentFormControls.SubscriptionIdToUpgrade).disable();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.existingVoiceProducts && !changes.existingVoiceProducts.firstChange) {
      this.voiceProductsAvailableForUpgrade = this.existingVoiceProducts?.filter(
        p =>
          !VoiceProduct.isActivationInProgress(p) &&
          (!p.mobileProductId || (this.productInCart && p.mobileProductId === this.productInCart.id))
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
